/**
 * Date helpers using date-fns
 *
 * This file provides common date formatting and manipulation utilities
 * to replace moment.js throughout the application.
 */

import {
  format as fnsFormat,
  parseISO,
  parse,
  isAfter,
  isBefore,
  formatDistanceToNow,
  addDays,
  addMonths,
  addYears,
  startOfDay,
  endOfDay,
  differenceInDays,
  differenceInHours,
  differenceInMinutes,
} from "date-fns";
import { toZonedTime, formatInTimeZone, fromZonedTime } from "date-fns-tz";
import Bugsnag from "@bugsnag/js";

// Regex to detect "M/D/YYYY, HH:mm:ss" or "MM/DD/YYYY, HH:mm:ss" format (from API responses)
// Note: This is US format (month first), matching editPost.js:646 which uses "MM/DD/YYYY, HH:mm:ss"
const LOCALE_DATE_REGEX = /^\d{1,2}\/\d{1,2}\/\d{4},\s\d{2}:\d{2}:\d{2}$/;

// Regex to detect "YYYY-MM-DD HH:mm" or "YYYY-MM-DD HH:mm:ss" format (schedule dates from .when property)
// This format has NO timezone info, so it must be interpreted in user's profile timezone
const SCHEDULE_DATE_REGEX = /^\d{4}-\d{2}-\d{2}\s\d{2}:\d{2}(:\d{2})?$/;

const hasToDate = (value: unknown): value is { toDate: () => Date } => {
  return (
    typeof value === "object" &&
    value !== null &&
    "toDate" in value &&
    typeof value.toDate === "function"
  );
};

/**
 * Common date format constants
 */
export const DATE_FORMATS = {
  ISO: "yyyy-MM-dd'T'HH:mm:ss",
  DATE_ONLY: "yyyy-MM-dd",
  TIME_ONLY: "HH:mm",
  // Note: date-fns uses "EEE" for abbreviated weekday (Mon, Tue), NOT "ddd" (which is moment.js)
  DATETIME_12H: "EEE d MMM, h:mm a",
  DATETIME_24H: "EEE d MMM, H:mm",
  FULL_DATETIME: "EEE d MMM yyyy H:mm",
  INSIGHTS_REQUEST: "yyyy-MM-dd'T'HH:mm:ss",
} as const;

/**
 * Format a date with a given format string
 *
 * @param date - Date string, Date object, or number (timestamp)
 * @param formatStr - Format string (defaults to ISO format)
 * @returns Formatted date string
 */
export const formatDate = (
  date: string | Date | number,
  formatStr: string = DATE_FORMATS.ISO
): string => {
  const d = typeof date === "string" ? parseISO(date) : new Date(date);
  return fnsFormat(d, formatStr);
};

/**
 * Format a date in a specific timezone
 *
 * @param date - Date string, Date object, or number (timestamp)
 * @param timezone - Timezone string (e.g., "America/New_York")
 * @param formatStr - Format string
 * @returns Formatted date string in the specified timezone
 */
export const formatDateInTimezone = (
  date: string | Date | number,
  timezone: string,
  formatStr: string = DATE_FORMATS.ISO
): string => {
  const d = typeof date === "string" ? parseISO(date) : new Date(date);
  return formatInTimeZone(d, timezone, formatStr);
};

/**
 * Check if a date is in the past
 *
 * @param date - Date to check
 * @returns true if date is in the past
 */
export const isDateInPast = (date: string | Date | number): boolean => {
  const d = typeof date === "string" ? parseISO(date) : new Date(date);
  return isBefore(d, new Date());
};

/**
 * Check if a date is in the future
 *
 * @param date - Date to check
 * @returns true if date is in the future
 */
export const isDateInFuture = (date: string | Date | number): boolean => {
  const d = typeof date === "string" ? parseISO(date) : new Date(date);
  return isAfter(d, new Date());
};

/**
 * Format distance to now (e.g., "2 hours ago")
 *
 * @param date - Date to compare
 * @returns Human-readable distance string
 */
export const timeAgo = (date: string | Date | number): string => {
  const d = typeof date === "string" ? parseISO(date) : new Date(date);
  return formatDistanceToNow(d, { addSuffix: true });
};

/**
 * Add days to a date
 *
 * @param date - Starting date
 * @param days - Number of days to add
 * @returns New date with days added
 */
export const addDaysToDate = (
  date: string | Date | number,
  days: number
): Date => {
  const d = typeof date === "string" ? parseISO(date) : new Date(date);
  return addDays(d, days);
};

/**
 * Add months to a date
 *
 * @param date - Starting date
 * @param months - Number of months to add
 * @returns New date with months added
 */
export const addMonthsToDate = (
  date: string | Date | number,
  months: number
): Date => {
  const d = typeof date === "string" ? parseISO(date) : new Date(date);
  return addMonths(d, months);
};

/**
 * Add years to a date
 *
 * @param date - Starting date
 * @param years - Number of years to add
 * @returns New date with years added
 */
export const addYearsToDate = (
  date: string | Date | number,
  years: number
): Date => {
  const d = typeof date === "string" ? parseISO(date) : new Date(date);
  return addYears(d, years);
};

/**
 * Get the start of day (00:00:00)
 *
 * @param date - Date to get start of
 * @returns Date at start of day
 */
export const getStartOfDay = (date: string | Date | number): Date => {
  const d = typeof date === "string" ? parseISO(date) : new Date(date);
  return startOfDay(d);
};

/**
 * Get the end of day (23:59:59)
 *
 * @param date - Date to get end of
 * @returns Date at end of day
 */
export const getEndOfDay = (date: string | Date | number): Date => {
  const d = typeof date === "string" ? parseISO(date) : new Date(date);
  return endOfDay(d);
};

/**
 * Get difference in days between two dates
 *
 * @param dateLeft - Later date
 * @param dateRight - Earlier date
 * @returns Number of days difference
 */
export const getDaysDifference = (
  dateLeft: string | Date | number,
  dateRight: string | Date | number
): number => {
  const left =
    typeof dateLeft === "string" ? parseISO(dateLeft) : new Date(dateLeft);
  const right =
    typeof dateRight === "string" ? parseISO(dateRight) : new Date(dateRight);
  return differenceInDays(left, right);
};

/**
 * Get difference in hours between two dates
 *
 * @param dateLeft - Later date
 * @param dateRight - Earlier date
 * @returns Number of hours difference
 */
export const getHoursDifference = (
  dateLeft: string | Date | number,
  dateRight: string | Date | number
): number => {
  const left =
    typeof dateLeft === "string" ? parseISO(dateLeft) : new Date(dateLeft);
  const right =
    typeof dateRight === "string" ? parseISO(dateRight) : new Date(dateRight);
  return differenceInHours(left, right);
};

/**
 * Get difference in minutes between two dates
 *
 * @param dateLeft - Later date
 * @param dateRight - Earlier date
 * @returns Number of minutes difference
 */
export const getMinutesDifference = (
  dateLeft: string | Date | number,
  dateRight: string | Date | number
): number => {
  const left =
    typeof dateLeft === "string" ? parseISO(dateLeft) : new Date(dateLeft);
  const right =
    typeof dateRight === "string" ? parseISO(dateRight) : new Date(dateRight);
  return differenceInMinutes(left, right);
};

/**
 * Convert a date to a specific timezone
 *
 * @param date - Date to convert
 * @param timezone - Target timezone
 * @returns Date in the target timezone
 */
export const toTimezone = (
  date: string | Date | number,
  timezone: string
): Date => {
  const d = typeof date === "string" ? parseISO(date) : new Date(date);
  return toZonedTime(d, timezone);
};

/**
 * Parse a date string to a Date object
 *
 * @param dateString - ISO date string
 * @returns Date object
 */
export const parseDate = (dateString: string): Date => {
  return parseISO(dateString);
};

/**
 * Safely parse a date input, handling various formats including:
 * - ISO 8601: "2026-01-12T17:18:32" or "2024-01-15T15:00:00-05:00"
 * - Space-separated: "2026-01-12 17:18:32" (Safari doesn't parse this natively)
 * - Space-separated without seconds: "2026-01-12 17:18"
 * - Locale format: "1/12/2026, 17:18:32" (M/D/YYYY US format from API responses)
 * - Date only: "2026-01-12"
 * - Timestamps (numbers)
 * - Date objects
 * - Moment objects (converted via toDate())
 * - Objects with toDate() method
 *
 * This function uses date-fns parse() with explicit format strings for reliability,
 * ensuring cross-browser compatibility (especially Safari).
 *
 * TIMEZONE HANDLING:
 * When a timezone is provided (e.g., "America/New_York") and the input is a
 * timezone-naive datetime string (space-separated or locale format), the time
 * is interpreted as being in that timezone and converted to UTC.
 * ISO strings with embedded timezone offsets are parsed correctly regardless.
 *
 * @param dateInput - Date string, number (timestamp), Date object, or object with toDate()
 * @param timezone - Optional IANA timezone (e.g., "America/New_York") for interpreting datetime strings
 * @returns Date object (may be Invalid Date if parsing fails)
 */
export const safeParseDateString = (
  dateInput:
    | string
    | number
    | Date
    | { toDate: () => Date }
    | null
    | undefined
    | unknown, // Accept unknown to capture unexpected types in Bugsnag
  timezone?: string
): Date => {
  if (dateInput === null || dateInput === undefined) {
    return new Date(NaN); // Return Invalid Date
  }

  // Already a Date object
  if (dateInput instanceof Date) {
    return dateInput;
  }

  // Number (timestamp)
  if (typeof dateInput === "number") {
    return new Date(dateInput);
  }

  // Object with toDate() method (Moment, dayjs, etc.)
  if (hasToDate(dateInput)) {
    return dateInput.toDate();
  }

  // String - handle various date formats
  if (typeof dateInput === "string") {
    if (!dateInput) {
      return new Date(NaN);
    }

    // Handle locale format: "M/D/YYYY, HH:mm:ss" or "MM/DD/YYYY, HH:mm:ss" (from API responses)
    // Note: This is US format (month first), matching editPost.js:646
    if (LOCALE_DATE_REGEX.test(dateInput)) {
      const naiveDate = parse(dateInput, "M/d/yyyy, HH:mm:ss", new Date());
      // If timezone provided, interpret as being in that timezone
      if (timezone) {
        return fromZonedTime(naiveDate, timezone);
      }
      return naiveDate;
    }

    // Handle schedule format: "YYYY-MM-DD HH:mm" or "YYYY-MM-DD HH:mm:ss"
    // This format has NO timezone info - it represents time in user's profile timezone
    if (SCHEDULE_DATE_REGEX.test(dateInput)) {
      // After regex validation with ^ and $ anchors, string is exactly:
      // - 16 chars for "YYYY-MM-DD HH:mm" (no seconds)
      // - 19 chars for "YYYY-MM-DD HH:mm:ss" (with seconds)
      const hasSeconds = dateInput.length === 19;
      const formatStr = hasSeconds ? "yyyy-MM-dd HH:mm:ss" : "yyyy-MM-dd HH:mm";
      const naiveDate = parse(dateInput, formatStr, new Date());

      if (timezone) {
        // Interpret as being in user's timezone and convert to UTC
        return fromZonedTime(naiveDate, timezone);
      }
      // Without timezone, return naive date (parse() already handles Safari compatibility)
      return naiveDate;
    }

    // ISO format with timezone offset (e.g., "2024-01-15T15:00:00-05:00")
    // parseISO correctly handles the embedded timezone info
    return parseISO(dateInput);
  }

  // Fallback for unexpected input types - log to Bugsnag for debugging
  // This allows us to discover new date formats in production and add support for them
  Bugsnag.notify(
    new Error("safeParseDateString received unexpected input"),
    (event) => {
      event.severity = "warning";
      event.addMetadata("dateInput", {
        value: String(dateInput),
        type: typeof dateInput,
        constructor:
          typeof dateInput === "object"
            ? (dateInput as object)?.constructor?.name
            : undefined,
      });
    }
  );

  // Attempt to parse unknown input - may work for some edge cases
  try {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    return new Date(dateInput as any);
  } catch {
    return new Date(NaN);
  }
};
